import { PlanOptions, type TPlanOptions } from '@constant'
import type { CollapsibleRequiredPlan } from '../components/CollapsibleList/types'
import { isPlanLocked } from '@themes/common/resolveNavRequiredPlan'
import { PLAN_FEATURE_ENTITLEMENTS } from './catalog'
import type { PlanFeatureKey } from './featureKeys'
import type {
  PlanFeatureEntitlement,
  PlanFeatureRequiredPlan,
  PlanFeatureSubscription,
} from './types'

/** CollapsibleList headerSwitch — excludes FREE (no upgrade CTA for free-tier requirement). */
function toCollapsibleRequiredPlan(
  plan: PlanFeatureRequiredPlan | undefined,
): CollapsibleRequiredPlan | undefined {
  if (!plan || plan === PlanOptions.Free) return undefined
  return plan as CollapsibleRequiredPlan
}

const PLAN_RANK: Record<string, number> = {
  [PlanOptions.Free]: 0,
  [PlanOptions.Starter]: 1,
  [PlanOptions.Professional]: 2,
  [PlanOptions.Premium]: 3,
}

export function getPlanFeatureEntitlement(
  key: PlanFeatureKey,
): PlanFeatureEntitlement | undefined {
  return PLAN_FEATURE_ENTITLEMENTS[key]
}

export function requiredPlanForFeature(
  key: PlanFeatureKey,
): PlanFeatureRequiredPlan | undefined {
  return PLAN_FEATURE_ENTITLEMENTS[key]?.requiredPlan
}

/**
 * Whether the shop subscription may use the feature.
 * Reuses `isPlanLocked` so FREE/Starter/Pro/Premium/Plus rules stay single-sourced.
 */
export function canUsePlanFeature(
  key: PlanFeatureKey,
  subscription: PlanFeatureSubscription,
): boolean {
  const required = requiredPlanForFeature(key)
  if (!required) return true
  return !isPlanLocked(subscription, required)
}

export function isPlanFeatureLocked(
  key: PlanFeatureKey,
  subscription: PlanFeatureSubscription,
): boolean {
  return !canUsePlanFeature(key, subscription)
}

/**
 * Theme setting keys whose `requiredPlan` is exactly `minPlan`
 * (or at least `minPlan` when `atLeast` is true).
 */
export function getThemeSettingKeysRequiring(
  minPlan: TPlanOptions,
  options?: { atLeast?: boolean },
): string[] {
  const atLeast = Boolean(options?.atLeast)
  const minRank = PLAN_RANK[minPlan] ?? 0
  const keys: string[] = []

  for (const entitlement of Object.values(PLAN_FEATURE_ENTITLEMENTS)) {
    if (entitlement.category !== 'theme_setting' || !entitlement.themeSettingKey) {
      continue
    }
    const plan = entitlement.requiredPlan
    if (plan === 'SHOPIFY_PLUS') continue
    const rank = PLAN_RANK[plan] ?? -1
    if (atLeast ? rank >= minRank : rank === minRank) {
      keys.push(entitlement.themeSettingKey)
    }
  }

  return [...new Set(keys)]
}

/**
 * Keys force-disabled on theme save when shop is below Professional.
 * Must stay identical to legacy PROFESSIONAL_PLUS_THEME_SETTING_KEYS.
 */
export function getThemeSettingKeysEnforcedOnSave(): string[] {
  const keys: string[] = []
  for (const entitlement of Object.values(PLAN_FEATURE_ENTITLEMENTS)) {
    if (
      entitlement.enforceOnSave &&
      entitlement.themeSettingKey &&
      entitlement.requiredPlan === PlanOptions.Professional
    ) {
      keys.push(entitlement.themeSettingKey)
    }
  }
  return keys
}

/** Bridge entitlement → legacy ItemMarkup / BadgeSpan `plan` badge. */
export function planBadgeForRequiredPlan(
  required: PlanFeatureRequiredPlan | undefined,
): { start?: boolean; pro?: boolean; pre?: boolean } | undefined {
  if (!required || required === 'SHOPIFY_PLUS') return undefined
  if (required === PlanOptions.Starter) return { start: true }
  if (required === PlanOptions.Professional) return { pro: true }
  if (required === PlanOptions.Premium) return { pre: true }
  return undefined
}

/** Convenience: feature key → ItemMarkup `plan` badge (undefined if unknown). */
export function planBadgeForFeature(
  key: PlanFeatureKey,
): { start?: boolean; pro?: boolean; pre?: boolean } | undefined {
  return planBadgeForRequiredPlan(requiredPlanForFeature(key))
}

/**
 * Props for CollapsibleList `headerSwitch` from entitlements.
 * `active: false` skips lock (e.g. metafields on old stores).
 */
export function planFeatureSwitchProps(
  key: PlanFeatureKey,
  subscription: PlanFeatureSubscription,
  options?: { active?: boolean },
): {
  disabled: boolean
  requiredPlan: CollapsibleRequiredPlan | undefined
} {
  if (options?.active === false) {
    return { disabled: false, requiredPlan: undefined }
  }
  return {
    disabled: isPlanFeatureLocked(key, subscription),
    requiredPlan: toCollapsibleRequiredPlan(requiredPlanForFeature(key)),
  }
}
